import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Gift, CreditCard, Mail, User, Phone, MessageSquare, Check, ArrowRight, Home } from 'lucide-react';
import axios from 'axios';
import { useTranslation } from '../hooks/useTranslation';
import LanguageSelector from './LanguageSelector';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8001';

const GiftCardPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [selectedAmount, setSelectedAmount] = useState(null);
  const [customAmount, setCustomAmount] = useState('');
  const [formData, setFormData] = useState({
    buyerName: '',
    buyerEmail: '',
    buyerPhone: '',
    recipientName: '',
    recipientEmail: '',
    message: '',
    deliveryMethod: 'email' // 'email' or 'physical'
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);

  const presetAmounts = [25, 50, 75, 100, 150, 200];

  const finalAmount = selectedAmount === 'custom' ? parseFloat(customAmount) || 0 : selectedAmount;

  const handleAmountSelect = (amount) => {
    setSelectedAmount(amount);
    if (amount !== 'custom') {
      setCustomAmount('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!finalAmount || finalAmount < 10) {
      alert('Please select or enter an amount of at least €10');
      return;
    }

    setIsSubmitting(true);
    try {
      const giftCardData = {
        ...formData,
        amount: finalAmount,
        currency: 'EUR',
        status: 'pending_payment',
        createdAt: new Date().toISOString()
      };

      const response = await axios.post(`${BACKEND_URL}/api/gift-cards`, giftCardData);
      
      if (response.data.success) {
        setOrderDetails(response.data.giftCard);
        setOrderSuccess(true);
        // Here you would integrate payment gateway (Stripe, PayPal, etc.)
        // For now, we'll show a success message
      }
    } catch (error) {
      console.error('Error creating gift card order:', error);
      alert('Error processing gift card order. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (orderSuccess) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-luxury-navy via-luxury-charcoal to-luxury-navy">
        {/* Navigation Header */}
        <nav className="fixed top-0 left-0 right-0 z-50 bg-luxury-navy/98 backdrop-blur-xl border-b border-luxury-gold/30 shadow-2xl">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between items-center h-20">
              <div className="flex-shrink-0 cursor-pointer" onClick={() => navigate('/')}>
                <img 
                  src="https://customer-assets.emergentagent.com/job_fine-dining-vaasa/artifacts/6i8d0v30_FB_IMG_1759419152155.jpg" 
                  alt="Sunsari Restaurant" 
                  className="h-14 w-auto max-w-[200px]"
                />
              </div>
              
              <div className="flex items-center space-x-4">
                <button
                  onClick={() => navigate('/')}
                  className="flex items-center gap-2 px-4 py-2 bg-luxury-gold/20 hover:bg-luxury-gold/30 text-white rounded-full transition-all"
                >
                  <Home className="w-4 h-4" />
                  <span className="hidden sm:inline">{t('nav.home')}</span>
                </button>
                <LanguageSelector />
              </div>
            </div>
          </div>
        </nav>
        
        <div className="flex items-center justify-center px-6 pt-32">
        <div className="bg-gradient-to-br from-green-500/20 to-emerald-500/20 border-2 border-green-400 rounded-3xl p-12 text-center max-w-2xl backdrop-blur-lg">
          <div className="w-24 h-24 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-6 animate-bounce">
            <Check className="w-14 h-14 text-white" />
          </div>
          <h2 className="text-4xl font-bold text-green-400 mb-4">Gift Card Order Received!</h2>
          <p className="text-white text-xl mb-4">Thank you, {formData.buyerName}!</p>
          <div className="bg-white/10 rounded-xl p-6 mb-6">
            <p className="text-white/90 text-lg mb-2">Gift Card Amount: <span className="font-bold text-amber-300">€{finalAmount}</span></p>
            <p className="text-white/80 text-sm">For: {formData.recipientName}</p>
            <p className="text-white/80 text-sm">{formData.recipientEmail}</p>
          </div>
          <p className="text-white/70 mb-6">We'll send you payment instructions via email shortly.</p>
          <button
            onClick={() => navigate('/')}
            className="px-8 py-3 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold rounded-xl transition-all"
          >
            Back to Home
          </button>
        </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-luxury-navy via-luxury-charcoal to-luxury-navy">
      {/* Navigation Header */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-luxury-navy/98 backdrop-blur-xl border-b border-luxury-gold/30 shadow-2xl">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-20">
            <div className="flex-shrink-0 cursor-pointer" onClick={() => navigate('/')}>
              <img 
                src="https://customer-assets.emergentagent.com/job_fine-dining-vaasa/artifacts/6i8d0v30_FB_IMG_1759419152155.jpg" 
                alt="Sunsari Restaurant" 
                className="h-14 w-auto max-w-[200px]"
              />
            </div>
            
            <div className="flex items-center space-x-4">
              <button
                onClick={() => navigate('/')}
                className="flex items-center gap-2 px-4 py-2 bg-luxury-gold/20 hover:bg-luxury-gold/30 text-white rounded-full transition-all"
              >
                <Home className="w-4 h-4" />
                <span className="hidden sm:inline">{t('nav.home')}</span>
              </button>
              <LanguageSelector />
            </div>
          </div>
        </div>
      </nav>
      
      <div className="max-w-4xl mx-auto px-6 pt-32 pb-16">
        {/* Header */}
        <div className="text-center mb-12">
          <div className="flex items-center justify-center gap-3 mb-4">
            <Gift className="w-12 h-12 text-amber-400" />
            <h1 className="text-4xl md:text-5xl font-bold text-white">Gift Cards</h1>
          </div>
          <p className="text-white/70 text-lg max-w-2xl mx-auto">
            Give the gift of authentic Nepalese & Indian cuisine
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-6">
          {/* All-in-One Form Card */}
          <div className="bg-gradient-to-br from-slate-900/95 to-slate-800/95 backdrop-blur-lg rounded-3xl p-8 border-2 border-amber-400/30">
            
            {/* Gift Card Preview */}
            <div className="mb-8">
              <div className="relative max-w-sm mx-auto">
                <div className="bg-gradient-to-br from-amber-500 via-orange-500 to-red-500 rounded-2xl p-6 shadow-2xl">
                  <div className="text-white">
                    <h3 className="text-2xl font-bold mb-1">SUNSARI</h3>
                    <p className="text-white/80 text-xs mb-4">Restaurant Gift Card</p>
                    <div className="bg-white/20 backdrop-blur-sm rounded-lg p-3 mb-3">
                      <p className="text-white/70 text-xs">VALUE</p>
                      <p className="text-3xl font-bold">€{finalAmount || '---'}</p>
                    </div>
                    <p className="text-white/70 text-xs">Valid for 12 months</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Amount Selection */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-amber-300 mb-4 flex items-center gap-2">
                <CreditCard className="w-5 h-5" /> Select Amount
              </h2>
              <div className="grid grid-cols-3 gap-3 mb-4">
                {presetAmounts.map((amount) => (
                  <button
                    key={amount}
                    type="button"
                    onClick={() => handleAmountSelect(amount)}
                    className={`p-3 rounded-lg font-bold text-lg transition-all ${
                      selectedAmount === amount
                        ? 'bg-amber-500 text-white scale-105'
                        : 'bg-white/10 text-white hover:bg-white/20'
                    }`}
                  >
                    €{amount}
                  </button>
                ))}
              </div>
              <div className="flex gap-3">
                <button
                  type="button"
                  onClick={() => handleAmountSelect('custom')}
                  className={`px-4 py-2 rounded-lg font-semibold transition-all ${
                    selectedAmount === 'custom'
                      ? 'bg-amber-500 text-white'
                      : 'bg-white/10 text-white hover:bg-white/20'
                  }`}
                >
                  Custom
                </button>
                {selectedAmount === 'custom' && (
                  <input
                    type="number"
                    min="10"
                    step="0.01"
                    value={customAmount}
                    onChange={(e) => setCustomAmount(e.target.value)}
                    placeholder="Min €10"
                    className="flex-1 px-4 py-2 bg-white/10 border border-white/20 rounded-lg text-white"
                  />
                )}
              </div>
            </div>

            {/* Your Information */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-blue-300 mb-4 flex items-center gap-2">
                <User className="w-5 h-5" /> Your Information
              </h2>
              <div className="grid md:grid-cols-2 gap-4">
                <input
                  type="text"
                  placeholder="Your Name *"
                  value={formData.buyerName}
                  onChange={(e) => setFormData({...formData, buyerName: e.target.value})}
                  className="px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50"
                  required
                />
                <input
                  type="email"
                  placeholder="Your Email *"
                  value={formData.buyerEmail}
                  onChange={(e) => setFormData({...formData, buyerEmail: e.target.value})}
                  className="px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50"
                  required
                />
                <input
                  type="tel"
                  placeholder="Your Phone *"
                  value={formData.buyerPhone}
                  onChange={(e) => setFormData({...formData, buyerPhone: e.target.value})}
                  className="px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50"
                  required
                />
              </div>
            </div>

            {/* Recipient Information */}
            <div className="mb-8">
              <h2 className="text-xl font-bold text-green-300 mb-4 flex items-center gap-2">
                <Mail className="w-5 h-5" /> Recipient Information
              </h2>
              <div className="grid md:grid-cols-2 gap-4 mb-4">
                <input
                  type="text"
                  placeholder="Recipient Name *"
                  value={formData.recipientName}
                  onChange={(e) => setFormData({...formData, recipientName: e.target.value})}
                  className="px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50"
                  required
                />
                <input
                  type="email"
                  placeholder="Recipient Email *"
                  value={formData.recipientEmail}
                  onChange={(e) => setFormData({...formData, recipientEmail: e.target.value})}
                  className="px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50"
                  required
                />
              </div>
              <textarea
                value={formData.message}
                onChange={(e) => setFormData({...formData, message: e.target.value})}
                className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-white/50"
                rows="2"
                placeholder="Personal message (optional)"
              />
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isSubmitting || !finalAmount || finalAmount < 10}
              className="w-full py-4 bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold text-lg rounded-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-3"
            >
              {isSubmitting ? 'Processing...' : `Purchase Gift Card - €${finalAmount || 0}`}
              <ArrowRight className="w-5 h-5" />
            </button>
            <p className="text-white/60 text-sm text-center mt-3">Payment instructions will be sent to your email</p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default GiftCardPage;
